import { useEffect, useRef, useState } from 'react'

export interface PlacementPointer {
  /** Zeigerstelle in CLIENT-Koordinaten. */
  x: number
  y: number
  /** Ob der Zeiger über der Canvas-Fläche steht. */
  overCanvas: boolean
}

interface UseCanvasPlacementDragOptions {
  /** Ob gerade eine Notiz scharfgestellt ist — nur dann wird gemeldet. */
  active: boolean
  /**
   * Ob der Zeiger überhaupt verfolgt wird. Auch wenn nichts scharfgestellt
   * ist: Sonst wüsste der Hook im Moment des Scharfstellens nicht, wo der
   * Zeiger steht, und das Vorschaubild erschiene erst bei der ersten Bewegung.
   */
  track: boolean
}

const pointerFrom = (e: PointerEvent): PlacementPointer => ({
  x: e.clientX,
  y: e.clientY,
  // Über einer Notiz ist das Ziel ein KIND der Fläche, nicht sie selbst —
  // `closest` deckt beides ab. Dasselbe Verfahren wie in
  // `useCanvasCursorAnchor`.
  overCanvas: (e.target as Element | null)?.closest?.('.react-flow') != null,
})

/**
 * Verfolgt den Zeiger für das Vorschaubild, das beim Ablegen an ihm hängt.
 *
 * **Verfolgt wird durchgehend, gemeldet nur beim Platzieren.** Der Zeiger wird
 * in ein Ref geschrieben, solange `track` gilt; erst mit `active` wandert er in
 * den State. Ohne diese Trennung erschiene das Vorschaubild erst bei der ersten
 * Mausbewegung — man drückt den Knopf, und es passiert sichtbar nichts.
 *
 * Die Meldung ist über `requestAnimationFrame` gedrosselt: Ein Rendervorgang je
 * Frame genügt, Pointer-Events kommen häufiger.
 *
 * @example
 * const pointer = useCanvasPlacementDrag({ active: placementMode !== null, track: isActiveTab })
 */
export function useCanvasPlacementDrag({
  active,
  track,
}: UseCanvasPlacementDragOptions): PlacementPointer | null {
  const [pointer, setPointer] = useState<PlacementPointer | null>(null)
  const latestRef = useRef<PlacementPointer | null>(null)
  const frameRef = useRef(0)
  // Über ein Ref gespiegelt, damit der Listener NICHT bei jedem Umschalten neu
  // gebunden werden muss — er läuft über die ganze Lebensdauer.
  const activeRef = useRef(active)
  activeRef.current = active

  useEffect(() => {
    if (!track) return

    const handleMove = (e: PointerEvent) => {
      latestRef.current = pointerFrom(e)
      if (!activeRef.current || frameRef.current) return
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = 0
        if (activeRef.current && latestRef.current) setPointer(latestRef.current)
      })
    }

    window.addEventListener('pointermove', handleMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', handleMove)
      cancelAnimationFrame(frameRef.current)
      frameRef.current = 0
      // Sonst überdauerte eine veraltete Stelle den Tabwechsel, und das
      // Vorschaubild erschiene beim nächsten Mal an einer, an der der Zeiger
      // längst nicht mehr steht. Der angezeigte Wert muss MIT weg — das Ref
      // allein zu leeren ließe ihn im State stehen.
      latestRef.current = null
      setPointer(null)
    }
  }, [track])

  // Beim Scharfstellen sofort mit der zuletzt bekannten Stelle beginnen —
  // darauf, dass sich der Zeiger noch bewegt, ist kein Verlass.
  useEffect(() => {
    setPointer(active ? latestRef.current : null)
  }, [active])

  return active ? pointer : null
}
